import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, ArrowLeft, Shield, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

export default function AdminForgotPasswordPage() {
  const [email, setEmail] = useState("admin@expertbase.ai");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [tokenCopied, setTokenCopied] = useState(false);
  const [step, setStep] = useState(1);

  // Password strength matching backend requirements
  const passwordStrength = {
    minLength: newPassword.length >= 8,
    hasUppercase: /[A-Z]/.test(newPassword),
    hasLowercase: /[a-z]/.test(newPassword),
    hasNumber: /\d/.test(newPassword),
  };
  const passwordValid = Object.values(passwordStrength).every(Boolean);

  const handleCopyToken = () => {
    navigator.clipboard.writeText(resetToken);
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 2000);
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter admin email");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/forgot-password`, { email });
      // SECURITY: API no longer returns token — user must check their email
      setStep(2);
      toast.success("If that email is registered, a reset token has been sent.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!passwordValid) {
      toast.error("Password must be 8+ chars with uppercase, lowercase, and a number");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/reset-password`, { 
        email,
        reset_token: resetToken,
        new_password: newPassword
      });
      setSubmitted(true);
      toast.success("Admin password reset successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex flex-col">
      {/* Header */}
      <header className="bg-charcoal/50 border-b border-slate-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <span className="font-display text-xl text-gold-gradient">ExpertBase</span>
          </Link>
          <Link 
            to="/admin/login" 
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Admin Login
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gold-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-gold-500" />
            </div>
            <h1 className="font-display text-3xl mb-2">Admin Password Reset</h1>
            <p className="text-slate-400">
              {submitted 
                ? "Your admin password has been reset" 
                : step === 1 
                  ? "Verify your admin email to receive a reset token"
                  : "Enter the token from your email and set a new password"
              }
            </p>
          </div>

          <div className="bg-charcoal border border-slate-800 p-8 rounded-sm">
            {submitted ? (
              <div className="text-center">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="text-slate-300 mb-6">Your admin password has been successfully reset.</p>
                <Link to="/admin/login">
                  <Button className="w-full bg-gold-500 hover:bg-gold-400 text-black font-semibold py-6 rounded-sm">
                    Go to Admin Login
                  </Button>
                </Link>
              </div>
            ) : step === 1 ? (
              <form onSubmit={handleRequestReset} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Admin Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="admin@expertbase.ai"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-12 bg-obsidian border-slate-700 focus:border-gold-500 h-12"
                      data-testid="admin-forgot-email"
                    />
                  </div>
                </div>

                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gold-500 hover:bg-gold-400 text-black font-semibold py-6 rounded-sm"
                  data-testid="admin-forgot-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Send Reset Token"
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-5">
                {/* Token input — user types token from their email */}
                <div className="p-4 bg-gold-500/10 border border-gold-500/40 rounded-sm">
                  <p className="text-xs text-slate-400 mb-2">Enter the reset token from your email (valid for 1 hour):</p>
                  <Input
                    type="text"
                    placeholder="e.g. A1B2C3D4"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    className="bg-obsidian border-slate-700 focus:border-gold-500 h-12 text-center font-mono text-lg tracking-widest text-gold-400"
                    data-testid="admin-reset-token-input"
                  />
                  <p className="text-xs text-slate-500 mt-2">Check your email inbox for the token.</p>
                </div>

                {/* New password */}
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Admin Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="Create a strong password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-obsidian border-slate-700 focus:border-gold-500 h-12"
                    data-testid="admin-new-password"
                  />
                  {/* Password strength checklist */}
                  {newPassword && (
                    <div className="grid grid-cols-2 gap-1 mt-2">
                      {[
                        { ok: passwordStrength.minLength, label: "8+ characters" },
                        { ok: passwordStrength.hasUppercase, label: "Uppercase letter" },
                        { ok: passwordStrength.hasLowercase, label: "Lowercase letter" },
                        { ok: passwordStrength.hasNumber, label: "Number" },
                      ].map(({ ok, label }) => (
                        <div key={label} className={`flex items-center gap-1 text-xs ${ok ? "text-green-400" : "text-slate-500"}`}>
                          <span>{ok ? "✓" : "○"}</span>
                          <span>{label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button 
                  type="submit"
                  disabled={loading || !passwordValid}
                  className="w-full bg-gold-500 hover:bg-gold-400 text-black font-semibold py-6 rounded-sm disabled:opacity-50"
                  data-testid="admin-reset-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Reset Admin Password"
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => { setStep(1); setResetToken(""); }}
                  className="w-full text-center text-sm text-slate-400 hover:text-white transition-colors"
                >
                  ← Try a different email
                </button>
              </form>
            )}
          </div>

          <p className="text-center text-slate-500 text-sm mt-6">
            Contact support if you need additional help.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
